import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { API, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "./ui/sheet";
import { Checkbox } from "./ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Calendar as CalendarPicker } from "./ui/calendar";
import { AssigneePicker } from "./AssigneePicker";
import { X, Plus, Trash2, Paperclip, Calendar, User, Flag, Layers, FileText, Download, MessageSquare, Send } from "lucide-react";

const initials = (name) => (name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

const SCRIBBLE_PATH = "M 10 16.91 s 79.8 -11.36 98.1 -11.34 c 22.2 0.02 -47.82 14.25 -33.39 22.02 c 12.61 6.77 124.18 -27.98 133.31 -17.28 c 7.52 8.38 -26.8 20.02 4.61 22.05 c 24.55 1.93 113.37 -20.36 113.37 -20.36";
const getPathAnimate = (isChecked) => ({ pathLength: isChecked ? 1 : 0, opacity: isChecked ? 1 : 0 });
const getPathTransition = (isChecked) => ({
  pathLength: { duration: 0.8, ease: "easeInOut" },
  opacity: { duration: 0.01, delay: isChecked ? 0 : 0.8 },
});

const inputCls = "w-full h-9 px-3 rounded-xl bg-slate-100/80 dark:bg-white/10 border border-slate-200/60 dark:border-white/10 text-sm dark:text-white focus:outline-none focus:ring-2 focus:ring-[#007AFF]/50 focus:bg-white dark:focus:bg-white/15 transition-all";

const toISODate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const DateField = ({ label, value, onChange, testId }) => (
  <div>
    <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 flex items-center gap-1 mb-1.5"><Calendar size={11} /> {label}</label>
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testId} className={`${inputCls} text-left flex items-center`}>
          <span className={value ? "text-slate-800" : "text-slate-400"}>
            {value ? new Date(value + "T00:00:00").toLocaleDateString("tr-TR", { day: "numeric", month: "short", year: "numeric" }) : "Tarih seç"}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 rounded-2xl w-auto" align="start">
        <CalendarPicker mode="single" selected={value ? new Date(value + "T00:00:00") : undefined}
          onSelect={(d) => d && onChange(toISODate(d))} initialFocus />
      </PopoverContent>
    </Popover>
  </div>
);

export const TaskDetailSheet = ({ task, columns, board, onClose, reload }) => {
  const { user } = useAuth();
  const [title, setTitle] = useState(task.title);
  const [description, setDescription] = useState(task.description || "");
  const [newSubtask, setNewSubtask] = useState("");
  const [newComment, setNewComment] = useState("");
  const [sendingComment, setSendingComment] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    setTitle(task.title);
    setDescription(task.description || "");
  }, [task.id]);

  const patch = async (updates) => {
    try {
      await api.patch(`/tasks/${task.id}`, updates);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
    reload();
  };

  const addSubtask = async (e) => {
    e.preventDefault();
    if (!newSubtask.trim()) return;
    const subtasks = [...(task.subtasks || []), { id: crypto.randomUUID(), title: newSubtask.trim(), done: false }];
    setNewSubtask("");
    await patch({ subtasks });
  };

  const toggleSubtask = (id) =>
    patch({ subtasks: (task.subtasks || []).map((s) => (s.id === id ? { ...s, done: !s.done } : s)) });

  const removeSubtask = (id) =>
    patch({ subtasks: (task.subtasks || []).filter((s) => s.id !== id) });

  const addComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || sendingComment) return;
    setSendingComment(true);
    try {
      await api.post(`/tasks/${task.id}/comments`, { text: newComment.trim() });
      setNewComment("");
      reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSendingComment(false);
    }
  };

  const removeComment = async (id) => {
    await api.delete(`/tasks/${task.id}/comments/${id}`);
    reload();
  };

  const uploadFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/tasks/${task.id}/attachments`, fd);
      toast.success("Dosya eklendi");
      reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const deleteTask = async () => {
    if (!window.confirm("Task silinsin mi?")) return;
    await api.delete(`/tasks/${task.id}`);
    onClose();
    reload();
  };

  const column = columns.find((c) => c.id === task.column_id);
  const subtasks = task.subtasks || [];
  const attachments = task.attachments || [];
  const comments = task.comments || [];

  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent data-testid="task-detail-sheet" className="w-full sm:max-w-lg overflow-y-auto p-0 [&>button]:hidden">
        <SheetTitle className="sr-only">Task detayı</SheetTitle>
        <SheetDescription className="sr-only">Task düzenleme paneli</SheetDescription>
        <div className="sticky top-0 z-10 backdrop-blur-xl bg-white/85 dark:bg-[#1C1C1E]/85 border-b border-slate-200/70 dark:border-white/10 px-6 py-4 flex items-center gap-3">
          <select
            data-testid="task-detail-status-select"
            value={task.column_id}
            onChange={(e) => patch({ column_id: e.target.value })}
            className="h-8 pl-3 pr-7 rounded-full text-xs font-semibold appearance-none cursor-pointer focus:outline-none border"
            style={{ background: `${column?.color}15`, color: column?.color, borderColor: `${column?.color}40` }}
          >
            {columns.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <div className="ml-auto flex items-center gap-1">
            <button data-testid="task-detail-delete-button" onClick={deleteTask}
              className="w-8 h-8 rounded-lg hover:bg-[#FF2D55]/10 text-slate-400 hover:text-[#FF2D55] flex items-center justify-center transition-colors">
              <Trash2 size={15} />
            </button>
            <button data-testid="task-detail-close-button" onClick={onClose}
              className="w-8 h-8 rounded-lg hover:bg-slate-100 dark:hover:bg-white/10 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 flex items-center justify-center transition-colors">
              <X size={16} />
            </button>
          </div>
        </div>

        <div className="px-6 py-5 space-y-6">
          <input
            data-testid="task-detail-title-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={() => title.trim() && title !== task.title && patch({ title })}
            className="w-full font-display text-xl font-semibold tracking-tight text-slate-900 dark:text-white focus:outline-none bg-transparent"
          />

          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 flex items-center gap-1 mb-1.5"><User size={11} /> Atananlar</label>
              <div data-testid="task-detail-assignees">
                <AssigneePicker board={board} value={task.assignees || []} onChange={(assignees) => patch({ assignees })} />
              </div>
            </div>
            <div className="col-span-2">
              <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 flex items-center gap-1 mb-1.5"><Layers size={11} /> Grup</label>
              <input data-testid="task-detail-group-input" defaultValue={task.group || ""} placeholder="örn: Design"
                onBlur={(e) => e.target.value !== (task.group || "") && patch({ group: e.target.value })} className={inputCls} />
            </div>
            <DateField label="Başlangıç" testId="task-detail-start-date-picker" value={task.start_date} onChange={(v) => patch({ start_date: v })} />
            <DateField label="Bitiş" testId="task-detail-due-date-picker" value={task.due_date} onChange={(v) => patch({ due_date: v })} />
            <div className="col-span-2">
              <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 flex items-center gap-1 mb-1.5"><Flag size={11} /> Öncelik</label>
              <div className="flex gap-2">
                {[["low", "Düşük", "#8E8E93"], ["medium", "Orta", "#FF9500"], ["high", "Yüksek", "#FF2D55"]].map(([val, label, color]) => (
                  <button key={val} data-testid={`task-priority-${val}`} onClick={() => patch({ priority: val })}
                    className={`h-8 px-3.5 rounded-full text-xs font-semibold border transition-all active:scale-95 ${task.priority === val ? "" : "opacity-40 hover:opacity-70"}`}
                    style={{ background: `${color}12`, color, borderColor: `${color}40` }}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 flex items-center gap-1 mb-1.5"><FileText size={11} /> Açıklama</label>
            <textarea
              data-testid="task-detail-description-textarea"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              onBlur={() => description !== (task.description || "") && patch({ description })}
              placeholder="Task detaylarını yazın..."
              rows={4}
              className="w-full px-3.5 py-3 rounded-xl bg-slate-100/80 border border-slate-200/60 text-sm focus:outline-none focus:ring-2 focus:ring-[#007AFF]/50 focus:bg-white transition-all resize-none leading-relaxed"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                Alt Görevler {subtasks.length > 0 && `· ${subtasks.filter((s) => s.done).length}/${subtasks.length}`}
              </label>
            </div>
            {subtasks.length > 0 && (
              <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden mb-3">
                <div className="h-full rounded-full bg-[#34C759] transition-all duration-300"
                  style={{ width: `${(subtasks.filter((s) => s.done).length / subtasks.length) * 100}%` }} />
              </div>
            )}
            <div className="space-y-1.5">
              {subtasks.map((s) => (
                <div key={s.id} data-testid="task-detail-subtask-item"
                  className="group flex items-center gap-2.5 rounded-xl px-3 py-2 hover:bg-slate-50 dark:hover:bg-white/5 transition-colors">
                  <Checkbox checked={s.done} onCheckedChange={() => toggleSubtask(s.id)}
                    className="rounded-md transition-colors duration-300 data-[state=checked]:bg-[#34C759] data-[state=checked]:border-[#34C759]" />
                  <span className="flex-1 min-w-0">
                    <span className="relative inline-block max-w-full align-middle">
                      <span className={`text-sm transition-colors duration-300 ${s.done ? "text-slate-400 dark:text-slate-500" : "text-slate-700 dark:text-slate-200"}`}>{s.title}</span>
                      <motion.svg width="340" height="32" viewBox="0 0 340 32" preserveAspectRatio="none"
                        className="absolute left-0 top-1/2 -translate-y-1/2 pointer-events-none z-20 w-full h-8">
                        <motion.path d={SCRIBBLE_PATH} vectorEffect="non-scaling-stroke" strokeWidth={2}
                          strokeLinecap="round" strokeMiterlimit={10} fill="none" initial={false}
                          animate={getPathAnimate(!!s.done)} transition={getPathTransition(!!s.done)}
                          className="stroke-slate-800 dark:stroke-slate-100" />
                      </motion.svg>
                    </span>
                  </span>
                  <button data-testid="task-detail-subtask-delete-button" onClick={() => removeSubtask(s.id)}
                    className="text-slate-300 hover:text-[#FF2D55] transition-colors">
                    <X size={13} />
                  </button>
                </div>
              ))}
            </div>
            <form onSubmit={addSubtask} className="flex items-center gap-2 mt-2">
              <input data-testid="task-detail-add-subtask-input" value={newSubtask} onChange={(e) => setNewSubtask(e.target.value)}
                placeholder="Alt görev ekle..." className={inputCls} />
              <button type="submit" data-testid="task-detail-add-subtask-button"
                className="h-9 w-9 shrink-0 rounded-xl bg-slate-900 dark:bg-white/15 text-white flex items-center justify-center hover:bg-slate-700 dark:hover:bg-white/25 active:scale-95 transition-all">
                <Plus size={15} />
              </button>
            </form>
          </div>

          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 flex items-center gap-1 mb-2">
              <Paperclip size={11} /> Dosyalar · {attachments.length}
            </label>
            <div className="space-y-2">
              {attachments.map((a) => (
                <div key={a.id} className="group flex items-center gap-3 rounded-xl border border-slate-200/70 dark:border-white/10 px-3.5 py-2.5 hover:bg-slate-50 dark:hover:bg-white/5 transition-colors">
                  {a.content_type?.startsWith("image/") ? (
                    <img src={`${API}/files/${a.storage_path}`} alt={a.filename} crossOrigin="use-credentials"
                      className="w-10 h-10 rounded-lg object-cover bg-slate-100" />
                  ) : (
                    <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center text-slate-400">
                      <FileText size={16} />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-700 dark:text-slate-200 truncate">{a.filename}</p>
                    <p className="text-[11px] text-slate-400">{(a.size / 1024).toFixed(0)} KB · {a.uploaded_by}</p>
                  </div>
                  {a.content_type?.startsWith("image/") && (
                    <button data-testid="task-attachment-cover-button"
                      onClick={() => patch({ cover_attachment_id: task.cover_attachment_id === a.id ? null : a.id })}
                      title={task.cover_attachment_id === a.id ? "Kapağı kaldır" : "Kart kapağı yap"}
                      className={`h-7 px-2 rounded-lg text-[10px] font-semibold transition-colors ${task.cover_attachment_id === a.id ? "bg-[#007AFF]/10 text-[#0066CC]" : "bg-slate-100 text-slate-400 hover:text-slate-600"}`}>
                      Kapak
                    </button>
                  )}
                  <a href={`${API}/files/${a.storage_path}`} target="_blank" rel="noreferrer" data-testid="task-attachment-download-link"
                    className="w-8 h-8 rounded-lg hover:bg-slate-200/70 text-slate-400 hover:text-slate-700 flex items-center justify-center transition-colors">
                    <Download size={14} />
                  </a>
                  <button data-testid="task-attachment-delete-button"
                    onClick={async () => { await api.delete(`/tasks/${task.id}/attachments/${a.id}`); reload(); }}
                    className="w-8 h-8 rounded-lg hover:bg-[#FF2D55]/10 text-slate-300 hover:text-[#FF2D55] flex items-center justify-center transition-colors">
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
            <button onClick={() => fileRef.current?.click()} disabled={uploading}
              className="mt-2 w-full rounded-xl border border-dashed border-slate-300 dark:border-white/15 py-3 text-xs font-medium text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:border-slate-400 dark:hover:border-white/25 transition-colors disabled:opacity-50">
              {uploading ? "Yükleniyor..." : "+ Dosya ekle (max 15MB)"}
            </button>
            <input ref={fileRef} data-testid="task-detail-file-upload-input" type="file" className="hidden"
              onChange={(e) => { uploadFile(e.target.files?.[0]); e.target.value = ""; }} />
          </div>

          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 flex items-center gap-1 mb-3">
              <MessageSquare size={11} /> Yorumlar · {comments.length}
            </label>
            <div className="space-y-4">
              {comments.map((c) => (
                <div key={c.id} data-testid="task-comment-item" className="flex gap-2.5">
                  <span className={`w-7 h-7 shrink-0 rounded-full text-white text-[10px] font-semibold flex items-center justify-center ${c.author_role === "admin" ? "bg-slate-900" : "bg-[#007AFF]"}`}>
                    {initials(c.author)}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">{c.author}</span>
                      <span className={`text-[9px] font-bold uppercase tracking-wider rounded-full px-1.5 py-0.5 ${c.author_role === "admin" ? "bg-slate-100 text-slate-500" : "bg-[#007AFF]/10 text-[#0066CC]"}`}>
                        {c.author_role === "admin" ? "Plena" : "Müşteri"}
                      </span>
                      <span className="text-[10px] text-slate-400">
                        {new Date(c.created_at).toLocaleString("tr-TR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                      </span>
                      {(user?.role === "admin" || user?.id === c.user_id) && (
                        <button data-testid="task-comment-delete-button" onClick={() => removeComment(c.id)}
                          className="ml-auto text-slate-300 hover:text-[#FF2D55] transition-colors">
                          <X size={12} />
                        </button>
                      )}
                    </div>
                    <p className="text-sm text-slate-600 dark:text-slate-300 mt-0.5 whitespace-pre-wrap break-words leading-relaxed">{c.text}</p>
                  </div>
                </div>
              ))}
              {comments.length === 0 && (
                <p className="text-xs text-slate-300">Henüz yorum yok. İlk yorumu siz yazın.</p>
              )}
            </div>
            <form onSubmit={addComment} className="flex items-center gap-2 mt-3">
              <input data-testid="task-detail-comment-input" value={newComment} onChange={(e) => setNewComment(e.target.value)}
                placeholder="Yorum yazın..." className={inputCls} />
              <button type="submit" data-testid="task-detail-comment-submit" disabled={sendingComment}
                className="h-9 w-9 shrink-0 rounded-xl bg-[#0066CC] text-white flex items-center justify-center hover:bg-[#0052A3] active:scale-95 transition-all disabled:opacity-50">
                <Send size={14} />
              </button>
            </form>
          </div>

          <p className="text-[11px] text-slate-300 pb-4">
            Oluşturan: {task.created_by} · {new Date(task.created_at).toLocaleDateString("tr-TR")}
          </p>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default TaskDetailSheet;
